<template>
  <div class="row">
    <div class="col-md-offset-1 col-md-10">
      <div class="card" v-loading="loading">
        <div class="card-content">
          <h4 class="purple text-center">{{ $t('marketingCoupons.couponDetails') }}</h4>
          <div class="row">
            <div class="col-md-5 col-md-offset-1">
              <div class="form-group">
                <label>{{ $t('marketingCoupons.name') }}*</label>
                <input type="text"
                       class="form-control"
                       :placeholder="$t('marketingCoupons.name')"
                       v-model="header.name">
                <small class="text-danger" v-if="headerErrors.name">{{ $t('validations.requiredField') }}</small>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label>{{ $t('marketingCoupons.validFromTo') }}*</label>
                <br/>
                <el-date-picker type="daterange"
                                v-model="header.fromto"
                                value-format="yyyy-MM-dd">
                </el-date-picker>
                <br/>
                <small class="text-danger" v-if="headerErrors.fromto">{{ $t('validations.requiredField') }}</small>
              </div>
            </div>
            <div class="col-md-1">
              <div class="form-group">
                <label>{{ $t('marketingCoupons.active') }}</label>
                <br/>
                <p-switch v-model="header.active" type="success"></p-switch>
              </div>
            </div>
          </div>
          <div class="row" v-if="pushCampaigns.length">
            <div class="col-md-10 col-md-offset-1">
              <label>{{ $t('marketingCoupons.pushCampaigns') }}:</label>
              <span v-for="campaign in pushCampaigns" :key="campaign.id" class="coupon-badge coupon-badge--campaign">{{ campaign.name }}</span>
            </div>
          </div>
          <hr>

          <forth-step v-if="loaded"
                      ref="forthStep"
                      standalone
                      @groupCustomers="handleGroupCustomers"
                      :userLabelLevels="userLabelLevels"
                      :modelFromParent="couponModel"
                      :couponsParent="coupons"
                      :customerGroups="customerGroups"
                      :selectedCustomerGroups="selectedCustomerGroups"
                      :checkedKeys="checkedKeys"
                      :couponError="couponError"
                      :route="route">
            <!-- Subscription coupon (above "applied directly"): applies only through an active subscription -->
            <div slot="before-auto-apply" class="form-group coupon-subscription" :class="{ 'is-on': coupons.is_subscription }">
              <p-checkbox v-model="coupons.is_subscription" :disabled="subscriptionLocked" class="text-purple" @input="onSubscriptionToggle">
                <h6 class="coupon-selection pad-to-7">{{ $t('marketingCoupons.isSubscription') }}</h6>
              </p-checkbox>
              <small class="text-muted coupon-subscription__hint">{{ $t('marketingCoupons.isSubscriptionHint') }}</small>
              <small v-if="subscriptionLockReason" class="coupon-subscription__lock"><i class="fa fa-lock"></i> {{ subscriptionLockReason }}</small>
              <div v-if="subscriptionLevels.length" class="coupon-subscription__levels">
                <label>{{ $t('marketingCoupons.subscriptionLevels') }}:</label>
                <span v-for="level in subscriptionLevels" :key="level.id" class="coupon-badge coupon-badge--subscription">{{ level.name }}</span>
              </div>
            </div>
            <!-- Automatic marketing coupon: given only by automatic campaigns -->
            <div slot="before-auto-apply" class="form-group coupon-automation" :class="{ 'is-on': coupons.is_automation }">
              <p-checkbox v-model="coupons.is_automation" :disabled="automationLocked">
                <h6 class="coupon-selection pad-to-7">{{ $t('marketingCoupons.isAutomation') }}</h6>
              </p-checkbox>
              <small class="text-muted coupon-automation__hint">{{ $t('marketingCoupons.isAutomationHint') }}</small>
              <small v-if="automationLockReason" class="coupon-automation__lock"><i class="fa fa-lock"></i> {{ automationLockReason }}</small>
              <div v-if="automationCampaigns.length" class="coupon-automation__campaigns">
                <label>{{ $t('marketingCoupons.automationCampaigns') }}:</label>
                <span v-for="campaign in automationCampaigns" :key="campaign.id" class="coupon-badge coupon-badge--automation">{{ campaign.name }}</span>
              </div>
            </div>
          </forth-step>

          <div class="text-center mar-to-10">
            <button class="btn btn-purple-light btn-fill btn-wd" :disabled="saving" @click.prevent="save()">{{ $t('general.save') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {DatePicker} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import ForthStep from '../Campaigns/Steps/ForthStep.vue'
  import { isSpinningWheelFreeProductCoupon } from '../Campaigns/spinningWheelCoupon'

  Vue.use(DatePicker)

  export default {
    components: {
      [DatePicker.name]: DatePicker,
      PSwitch,
      ForthStep
    },
    props: {
      route: {
        type: Object,
        default: () => ({})
      },
      // opened inside another page (Abonamente / Marketing automat > "New coupon"): emits saved instead of closing the route modal
      embedded: {
        type: Boolean,
        default: false
      },
      // new coupon created from a subscription: "subscription coupon" ticked and locked
      forceSubscription: {
        type: Boolean,
        default: false
      },
      // new coupon created from an automatic campaign: "used in automatic marketing" ticked and locked
      forceAutomation: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        loading: false,
        loaded: false,
        saving: false,
        header: {
          name: '',
          fromto: null,
          active: true
        },
        headerErrors: {},
        pushCampaigns: [],
        // subscription levels giving this coupon (is_subscription coupons)
        subscriptionLevels: [],
        // automatic campaigns giving this coupon (is_automation coupons)
        automationCampaigns: [],
        // ForthStep keeps the order sources on its "model"
        couponModel: {type: 'COUPON', order_source_ids: []},
        coupons: {},
        userLabelLevels: [],
        customerGroups: [],
        selectedCustomerGroups: [],
        checkedKeys: [],
        couponError: null
      }
    },
    computed: {
      couponId () {
        return this.route.params && this.route.params.id
      },
      // can't untick while subscriptions give it, can't tick while push campaigns announce it
      subscriptionLockReason () {
        if (this.forceSubscription) {
          return ''
        }
        // a coupon can't be both a subscription and an automatic marketing coupon
        if (!this.coupons.is_subscription && this.coupons.is_automation) {
          return this.$t('marketingCoupons.subscriptionAutomationExclusive')
        }
        if (this.coupons.is_subscription && this.subscriptionLevels.length) {
          return this.$t('marketingCoupons.subscriptionLockedUsed')
        }
        if (!this.coupons.is_subscription && this.pushCampaigns.length) {
          return this.$t('marketingCoupons.subscriptionLockedCampaign')
        }
        return ''
      },
      subscriptionLocked () {
        return this.forceSubscription || this.forceAutomation || !!this.subscriptionLockReason
      },
      // can't untick while automatic campaigns give it, can't tick while push campaigns announce it
      automationLockReason () {
        if (this.forceAutomation) {
          return ''
        }
        if (!this.coupons.is_automation && this.coupons.is_subscription) {
          return this.$t('marketingCoupons.subscriptionAutomationExclusive')
        }
        if (this.coupons.is_automation && this.automationCampaigns.length) {
          return this.$t('marketingCoupons.automationLockedUsed')
        }
        if (!this.coupons.is_automation && this.pushCampaigns.length) {
          return this.$t('marketingCoupons.automationLockedCampaign')
        }
        return ''
      },
      automationLocked () {
        return this.forceAutomation || this.forceSubscription || !!this.automationLockReason
      }
    },
    created () {
      this.coupons = this.emptyCoupon()
      this.getUserLabeling()
      this.getGroups()
      if (this.couponId) {
        this.getCoupon()
      } else {
        this.loaded = true
      }
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      emptyCoupon () {
        return {
          type: 'PERCENTAGE',
          usage_type: null,
          discount: null,
          from_value: 0,
          coupon_code: null,
          delivery_methods: [],
          interval_settings: [],
          user_label_selected: null,
          coupons_codes: [],
          usage_for_categories: [],
          usage_for_products: [],
          free_product_id: null,
          min_order_value: null,
          is_subscription: this.forceSubscription,
          // a subscription coupon is cumulative by default
          is_cumulative: this.forceSubscription,
          is_automation: this.forceAutomation
        }
      },
      onSubscriptionToggle (value) {
        // a subscription coupon is always cumulative (the checkbox is locked)
        if (value) {
          this.$set(this.coupons, 'is_cumulative', true)
        }
      },
      handleGroupCustomers (e) {
        this.selectedCustomerGroups = e
      },
      getCoupon () {
        this.loading = true
        this.apiRequest('/coupon/' + this.couponId, 'GET', this.requestData(), (response) => {
          this.loading = false
          if (!(response.status === true && response.data && response.data[0])) {
            return
          }
          const coupon = response.data[0]

          this.header.name = coupon.name
          this.header.active = !!coupon.active
          this.header.fromto = coupon.datetime_validfrom && coupon.datetime_validto
            ? [coupon.datetime_validfrom.substr(0, 10), coupon.datetime_validto.substr(0, 10)]
            : null
          this.pushCampaigns = coupon.push_campaigns || []
          this.subscriptionLevels = coupon.subscription_levels || []
          coupon.is_subscription = !!coupon.is_subscription
          this.automationCampaigns = coupon.automation_campaigns || []
          coupon.is_automation = !!coupon.is_automation
          this.couponModel = {type: 'COUPON', order_source_ids: coupon.order_source_ids || []}

          // same preparation as the (old) campaign wizard
          if (coupon.waterfall_discounts && coupon.waterfall_discounts.length) {
            coupon.from_value = coupon.waterfall_discounts[0].from_value
            coupon.discount = coupon.waterfall_discounts[0].discount_value
            coupon.waterfall_discounts.shift()
          }
          if (!coupon.from_value) {
            coupon.from_value = 0
          }
          if (coupon.group_customers) {
            let groups = coupon.group_customers
            if (typeof groups === 'string') {
              try {
                groups = JSON.parse(groups)
              } catch (e) {
                groups = []
              }
            }
            if (groups && groups.length) {
              this.selectedCustomerGroups = groups
            }
          }
          if (coupon.free_product_id != null) {
            const freeProductId = coupon.free_product_id
            coupon.free_product_id = typeof freeProductId === 'object'
              ? parseInt(freeProductId.id || freeProductId.product_id, 10) || null
              : parseInt(freeProductId, 10) || null
          }
          if (!Array.isArray(coupon.coupons_codes)) {
            coupon.coupons_codes = []
          }
          coupon.user_label_selected = this.userLabelLevels.find(el => el.key === coupon.user_label_id) || null

          this.checkedKeys = Object.values(coupon.usage_for_products || []).concat(Object.values(coupon.usage_for_categories || []))
          this.coupons = coupon
          this.loaded = true
        })
      },
      getUserLabeling () {
        this.apiRequest('/userlabeling', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.userLabelLevels = response.data.items.map(el => ({
              key: el.id,
              label: el.name
            }))
            if (this.coupons && this.coupons.user_label_id) {
              this.$set(this.coupons, 'user_label_selected', this.userLabelLevels.find(el => el.key === this.coupons.user_label_id) || null)
            }
          }
        })
      },
      getGroups () {
        this.apiRequest('/customersgroup/get-groups', 'POST', this.requestData(), (response) => {
          if (response.status === true) {
            this.customerGroups = response.data
          }
        })
      },
      validateHeader () {
        this.headerErrors = {}
        if (!this.header.name || !String(this.header.name).trim()) {
          this.$set(this.headerErrors, 'name', true)
        }
        if (!this.header.fromto || !this.header.fromto[0] || !this.header.fromto[1]) {
          this.$set(this.headerErrors, 'fromto', true)
        }
        return !Object.keys(this.headerErrors).length
      },
      save () {
        const headerValid = this.validateHeader()
        Promise.resolve(this.$refs.forthStep.validate()).then((couponValid) => {
          if (headerValid && couponValid) {
            this.submit()
          }
        })
      },
      buildPayload () {
        const forthStep = this.$refs.forthStep
        // same order as the old campaign wizard: the transformations run on real arrays,
        // copyObject (arrays => objects, null => {}) only at the end, so the API receives the same payload.
        // A deep clone so a failed save does not alter the form (waterfall unshift).
        const coupon = JSON.parse(JSON.stringify(forthStep.coupons))

        if (coupon.user_label_selected) {
          coupon.user_label_id = coupon.user_label_selected.key
        } else {
          coupon.user_label_id = ''
        }
        delete coupon.user_label_selected

        if (coupon.type !== 'FREE_PRODUCT') {
          coupon.min_order_value = null
          if (coupon.is_waterfall || parseInt(coupon.from_value)) {
            if (parseInt(coupon.from_value) || parseInt(coupon.from_value) === 0) {
              if (!Array.isArray(coupon.waterfall_discounts)) {
                coupon.waterfall_discounts = []
              }
              coupon.waterfall_discounts.unshift({
                from_value: coupon.from_value,
                discount_value: coupon.discount
              })
              coupon.is_waterfall = true
              delete coupon.from_value
            }
            if (!parseInt(coupon.from_value) && !coupon.waterfall_discounts.length) {
              coupon.is_waterfall = false
            }
          }
        } else {
          delete coupon.discount
          delete coupon.is_waterfall
          delete coupon.waterfall_discounts
          delete coupon.waterfall_restriction
          delete coupon.from_value
          if (coupon.min_order_value === '' || coupon.min_order_value === undefined) {
            coupon.min_order_value = null
          }
        }
        if (!coupon.waterfall_restriction) {
          coupon.waterfall_restriction = false
        }
        if (!coupon.coupon_has_multiple_codes) {
          coupon.coupon_has_multiple_codes = false
        }

        const data = this.copyObject(coupon)
        if (data.type === 'FREE_PRODUCT' && coupon.free_product_id != null) {
          const freeProductId = coupon.free_product_id
          data.free_product_id = typeof freeProductId === 'object'
            ? parseInt(freeProductId.id || freeProductId.product_id, 10) || null
            : parseInt(freeProductId, 10) || null
        }

        if (isSpinningWheelFreeProductCoupon(coupon, forthStep.model)) {
          data.usage_for_categories = '[]'
          data.usage_for_products = '[]'
        } else if (data.usage_type === 'PRODUCTS') {
          const tree = forthStep.$refs.tree
          const checkedNodes = tree.getCheckedNodes()
          const level1Nodes = checkedNodes.filter(node => node.level === 1).map(node => node.id)
          const level2Nodes = checkedNodes
            .filter(node => node.level === 2)
            .filter(node => !level1Nodes.includes(tree.getNode(node).parent.data.id))
            .map(node => node.id)
          data.usage_for_categories = JSON.stringify(level1Nodes)
          data.usage_for_products = JSON.stringify(level2Nodes)
        } else {
          data.usage_for_categories = null
          data.usage_for_products = null
        }

        // after copyObject the lists are objects => Object.values
        data.delivery_methods = JSON.stringify(Object.values(data.delivery_methods || {}).map(method => method.key))
        data.location_ids = JSON.stringify(Object.values(data.location_ids || {}).map(location => location.id))
        data.group_customers = JSON.stringify(this.selectedCustomerGroups)

        if (typeof data.use_time_interval !== 'undefined' && (data.use_time_interval === '' || typeof data.use_time_interval === 'object')) {
          data.use_time_interval = 0
        }

        const orderSourceIds = forthStep.model && Array.isArray(forthStep.model.order_source_ids)
          ? forthStep.model.order_source_ids
          : []
        data.order_source_ids = JSON.stringify(orderSourceIds)

        data.name = this.header.name
        data.active = this.header.active ? 1 : 0
        data.is_subscription = coupon.is_subscription ? 1 : 0
        if (data.is_subscription) {
          // the API clears them too: a subscription coupon has no code
          delete data.coupon_code
          delete data.coupons_codes
        }
        data.is_automation = coupon.is_automation ? 1 : 0
        if (data.is_automation) {
          // one optional informative code only (the API clears the multiple codes too)
          delete data.coupons_codes
        }
        data.datetime_validfrom = this.header.fromto[0]
        data.datetime_validto = this.header.fromto[1]

        delete data.push_campaigns
        delete data.subscription_levels
        delete data.automation_campaigns
        delete data.qr_url
        delete data.coupons_codes_count
        delete data.order_sources_mask
        delete data.campaign_id
        delete data.company_id
        delete data.created_at
        delete data.updated_at
        delete data.deleted_at
        delete data.created_by
        delete data.updated_by
        delete data.deleted_by

        return {
          ...data,
          ...this.requestData()
        }
      },
      submit () {
        const data = this.buildPayload()
        const url = this.couponId ? '/coupon/' + this.couponId : '/coupon'
        const method = this.couponId ? 'PUT' : 'POST'

        this.saving = true
        this.couponError = null
        this.apiRequest(url, method, data, (response) => {
          this.saving = false
          if (response.status === true) {
            swal({
              title: '',
              text: this.$t('marketingCoupons.couponSaved'),
              type: 'success',
              showConfirmButton: false,
              timer: 1500
            }).catch(() => {})
            if (this.embedded) {
              this.$emit('saved', (response.data && response.data[0]) || null)
              return
            }
            this.goBack()
            return
          }

          const errors = response.errors || {}
          if (errors.coupon_code) {
            this.couponError = Array.isArray(errors.coupon_code) ? errors.coupon_code[0] : errors.coupon_code
          }
          if (this.$refs.forthStep) {
            this.$refs.forthStep.handleIntervalErrors(errors)
          }
          const messages = Object.keys(errors).map(key => Array.isArray(errors[key]) ? errors[key][0] : errors[key])
          swal('', messages.join(' ') || this.$t('validations.wentWrong'), 'error').catch(() => {})
        })
      },
      goBack () {
        this.$bus.$emit('refresh')
        this.$bus.$emit('close-modal')
      }
    }
  }
</script>
<style>
  .coupon-badge {
    display: inline-block;
    margin: 2px 4px;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.4;
  }
  .coupon-badge--campaign {
    background: #ede7f6;
    color: #5e35b1;
    border: 1px solid #d1c4e9;
  }
  .coupon-badge--subscription {
    background: #e3f2fd;
    color: #1565c0;
    border: 1px solid #bbdefb;
  }
  .coupon-subscription {
    margin-top: 6px;
    padding: 10px 14px;
    border: 1px dashed #d1c4e9;
    border-radius: 8px;
  }
  .coupon-subscription.is-on {
    border-style: solid;
    border-color: #90caf9;
    background: #f5faff;
  }
  .coupon-subscription__hint,
  .coupon-subscription__lock {
    display: block;
    margin-top: 2px;
  }
  .coupon-subscription__lock {
    color: #8d6e00;
  }
  .coupon-subscription__levels {
    margin-top: 6px;
  }
  .coupon-badge--automation {
    background: #f3eefb;
    color: #5e35b1;
    border: 1px solid #b39ddb;
  }
  .coupon-automation {
    margin-top: 6px;
    padding: 10px 14px;
    border: 1px dashed #d1c4e9;
    border-radius: 8px;
  }
  .coupon-automation.is-on {
    border-style: solid;
    border-color: #b39ddb;
    background: #faf7fe;
  }
  .coupon-automation__hint,
  .coupon-automation__lock {
    display: block;
    margin-top: 2px;
  }
  .coupon-automation__lock {
    color: #8d6e00;
  }
  .coupon-automation__campaigns {
    margin-top: 6px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/DiscountCoupons/CouponForm.vue